import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { fetchPresets } from '@/services/pipelineApi';
import type { RunPreset } from '@/types/pipeline';

/** Custom event name used to signal preset data has changed */
export const PRESET_CHANGED_EVENT = 'qs:presets-changed';

/** Dispatch this from any component to trigger a global preset refresh */
export function notifyPresetsChanged(): void {
  globalThis.dispatchEvent(new CustomEvent(PRESET_CHANGED_EVENT));
}

interface PresetNameContextValue {
  /** Map from preset ID to current preset name */
  presetNameMap: Map<string, string>;
  /** Force refresh presets (e.g. after a rename) */
  refreshPresets: () => void;
}

const PresetNameContext = createContext<PresetNameContextValue>({
  presetNameMap: new Map(),
  refreshPresets: () => {},
});

export function PresetNameProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [presets, setPresets] = useState<RunPreset[]>([]);

  const load = useCallback(() => {
    fetchPresets().then(setPresets).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  // Listen for custom event to refresh presets immediately
  useEffect(() => {
    const handler = () => load();
    globalThis.addEventListener(PRESET_CHANGED_EVENT, handler);
    return () => globalThis.removeEventListener(PRESET_CHANGED_EVENT, handler);
  }, [load]);

  const presetNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of presets) {
      map.set(p.id, p.name);
    }
    return map;
  }, [presets]);

  const value = useMemo(() => ({ presetNameMap, refreshPresets: load }), [presetNameMap, load]);

  return (
    <PresetNameContext.Provider value={value}>
      {children}
    </PresetNameContext.Provider>
  );
}

export function usePresetNames() {
  return useContext(PresetNameContext);
}

/**
 * Resolve the display name for a pipeline run using the latest preset names.
 * Priority: display_name > current preset name (by preset_id) > preset_name on run > formatted template_name
 */
export function resolveRunDisplayName(
  run: { display_name?: string; preset_id?: string | null; preset_name?: string | null; template_name: string },
  presetNameMap: Map<string, string>,
): string {
  if (run.display_name) return run.display_name;
  if (run.preset_id && presetNameMap.has(run.preset_id)) {
    return presetNameMap.get(run.preset_id)!;
  }
  if (run.preset_name) return run.preset_name;
  const name = run.template_name.replaceAll('_', ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
}
